import React, { useEffect, useState } from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { api, formatApiError } from "@/shared/lib/api";
import { formatTime12 } from "@/shared/lib/format";
import { useAuth } from "@/shared/context/AuthContext";
import SubPageShell from "@/apps/rider/components/SubPageShell";

const CATEGORIES = [
  { id: "ride",    label: "Ride issue",    hint: "Pickup, driver, route, timing" },
  { id: "payment", label: "Payment issue", hint: "Charges, refunds, receipts" },
  { id: "safety",  label: "Safety concern", hint: "Comfort, conduct, anything urgent" },
  { id: "other",   label: "Other",         hint: "Anything else" },
];

/**
 * /support/report — structured issue report with optional trip-id picker.
 */
export default function ReportIssue() {
  const { user } = useAuth();
  const [category, setCategory] = useState("ride");
  const [tripId, setTripId] = useState("");
  const [trips, setTrips] = useState([]);
  const [description, setDescription] = useState("");
  const [replyEmail, setReplyEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);

  // Pull a small list of recent trips so the rider can pick one without typing the id.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/trips/me");
        const list = Array.isArray(data) ? data.slice(0, 12) : [];
        setTrips(list);
      } catch (_) {
        // Best-effort — the form still works without the dropdown.
      }
    })();
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 8) {
      toast.error("Please describe what happened in a sentence or two.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/report", {
        category,
        trip_id: tripId || undefined,
        description: description.trim(),
        reply_email: (replyEmail || "").trim() || undefined,
      });
      setTicket(data?.ticket || null);
      toast.success("Issue reported.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <SubPageShell eyebrow="Support" title="Report an issue"
        subtitle="Sign in so we can connect your report to your account."
        back="/help" backLabel="Help center" testId="report-page">
        <Link to="/login" className="inline-flex items-center gap-2 bg-[#0A1128] text-[#F9F7F4] px-5 py-3 rounded-xl text-[12px] tracking-[0.18em] uppercase font-semibold">Sign in</Link>
      </SubPageShell>
    );
  }

  if (ticket) {
    return (
      <SubPageShell eyebrow="Support" title="Issue reported"
        subtitle="A real person will follow up shortly."
        back="/help" backLabel="Help center" testId="report-page">
        <div className="bg-white border hairline rounded-2xl p-6 text-center" data-testid="report-success">
          <div className="w-12 h-12 mx-auto bg-[#FCEADE] text-[#ed6500] rounded-2xl inline-flex items-center justify-center">
            <CheckCircle2 size={20} />
          </div>
          <div className="font-serif text-xl text-[#0A1128] mt-3">Thanks for letting us know</div>
          <p className="text-sm text-[#515E7B] mt-2">We'll review and reach back via email.</p>
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] mt-4">Ticket</div>
          <div className="font-serif text-lg text-[#0A1128] tracking-wider" data-testid="report-ticket">{ticket}</div>
          <button
            type="button"
            onClick={() => { setTicket(null); setDescription(""); setTripId(""); }}
            className="mt-5 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#0A1128] hover:text-[#ed6500]"
            data-testid="report-send-another"
          >
            Report another issue
          </button>
        </div>
      </SubPageShell>
    );
  }

  return (
    <SubPageShell
      eyebrow="Support"
      title="Report an issue"
      subtitle="Tell us what went wrong — we read every report."
      back="/help"
      backLabel="Help center"
      testId="report-page"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="report-form">
        {/* Category */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] block mb-2">Category</label>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Category">
            {CATEGORIES.map((c) => {
              const selected = category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setCategory(c.id)}
                  className={`text-left p-3 rounded-xl border transition-colors ${
                    selected
                      ? "bg-[#0A1128] text-[#F9F7F4] border-[#0A1128]"
                      : "bg-white text-[#0A1128] border-[#E5E1D8] hover:border-[#ed6500]/50"
                  }`}
                  data-testid={`report-cat-${c.id}`}
                  data-selected={selected}
                >
                  <div className="text-[12.5px] font-semibold">{c.label}</div>
                  <div className={`text-[10.5px] mt-0.5 ${selected ? "text-[#F9F7F4]/70" : "text-[#515E7B]"}`}>{c.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Trip selector */}
        {trips.length > 0 && (
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] block mb-2">Which trip? (optional)</label>
            <select
              value={tripId}
              onChange={(e) => setTripId(e.target.value)}
              className="w-full bg-white border hairline rounded-xl px-3 py-2.5 text-[14px] text-[#0A1128] focus:outline-none focus:border-[#ed6500]/60"
              data-testid="report-trip-select"
            >
              <option value="">— Not tied to a specific trip —</option>
              {trips.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.date} · {formatTime12(t.pickup_time)} · {t.direction === "return" ? "Return" : "Outbound"}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Reply email */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] block mb-1">Reply email</label>
          <input
            type="email"
            value={replyEmail}
            onChange={(e) => setReplyEmail(e.target.value)}
            placeholder="we@reach.you"
            className="input-line w-full"
            data-testid="report-reply-email"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] block mb-1">What happened?</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Walk us through what went wrong, including times if relevant."
            rows={6}
            maxLength={4000}
            className="w-full bg-white border hairline rounded-xl px-3 py-2 text-[14px] text-[#0A1128] placeholder:text-[#515E7B] focus:outline-none focus:border-[#ed6500]/60 resize-none"
            data-testid="report-description"
            required
          />
          <div className="text-[10px] text-[#515E7B] mt-1 text-right">{description.length}/4000</div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#0A1128] text-[#F9F7F4] py-3 rounded-xl text-[12px] tracking-[0.2em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="report-submit"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {submitting ? "Sending…" : "Submit report"}
        </button>
      </form>
    </SubPageShell>
  );
}
